import React, { useState, useEffect } from "react";
import { useForm } from "react-hook-form";
import { useTheme } from "@mui/material/styles";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { useNavigate, Link } from "react-router-dom";

import { Helmet } from "react-helmet-async";
import {
  Paper,
  Box,
  Button,
  TextField,
  Typography,
  Container,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Checkbox,
  FormControlLabel,
  CircularProgress,
  FormHelperText,
  IconButton,
  InputAdornment,
  Grid,
  Tooltip,
  Divider,
} from "@mui/material";
import { Visibility, VisibilityOff, InfoOutlined } from "@mui/icons-material";
import { registerUser } from "../api/auth";
import FormError from "../components/Forms/FormError";
import { useTranslation } from "react-i18next";

// Helper component for consistent TextField with react-hook-form
const FormInput = ({
  label,
  name,
  register,
  error,
  helperText,
  type = "text",
  size = "small",
  autoFocus = false,
  InputProps, // forwardable
  startAdornmentText, // optional sugar
}) => {
  return (
    <TextField
      label={label}
      variant="outlined"
      fullWidth
      margin="normal"
      size={size}
      {...register(name)}
      error={!!error}
      helperText={error ? error.message : helperText}
      type={type}
      autoFocus={autoFocus}
      inputProps={{
        "aria-describedby": error ? `${name}-error-text` : undefined,
      }}
      FormHelperTextProps={{ id: `${name}-error-text` }}
      // 👇 this was missing
      InputProps={{
        ...InputProps,
        startAdornment:
          InputProps?.startAdornment ??
          (startAdornmentText ? (
            <InputAdornment position="start">
              {startAdornmentText}
            </InputAdornment>
          ) : undefined),
      }}
    />
  );
};

const passwordStrength = (password) => {
  // Simple heuristic:
  if (!password) return 0;
  let score = 0;
  if (password.length >= 6) score += 1;
  if (/[A-Z]/.test(password)) score += 1;
  if (/[0-9]/.test(password)) score += 1;
  if (/[^A-Za-z0-9]/.test(password)) score += 1;
  return score;
};

const Register = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const theme = useTheme();
  const [errorMessage, setErrorMessage] = useState("");

  const registerSchema = yup.object().shape({
    first_name: yup.string().trim().required(t("errors.first_name_required")),
    last_name: yup.string().trim().required(t("errors.last_name_required")),
    phone: yup
      .string()
      .transform((value) => {
        if (!value) return value; // keep empty string for required check
        let v = value.replace(/\s+/g, ""); // remove spaces
        if (!v.startsWith("+")) {
          v = "+371" + v; // prepend +371 if no code
        }
        return v;
      })
      .matches(/^\+[0-9]{8,15}$/, t("errors.phone_invalid")) // validate full number
      .required(t("errors.phone_required")),

    email: yup
      .string()
      .trim()
      .lowercase()
      .email(t("errors.email_invalid"))
      .required(t("errors.email_required")),
    password: yup
      .string()
      .required(t("errors.password_required"))
      .min(6, t("errors.password_min")),
    agree_to_terms: yup
      .boolean()
      .oneOf([true], t("errors.terms_required"))
      .required(),
  });

  const {
    register,
    handleSubmit,
    watch,
    formState: { errors },
  } = useForm({
    resolver: yupResolver(registerSchema),
  });

  const [loading, setLoading] = useState(false);

  const strengthLabel = [
    t("forms.password_too_weak"),
    t("forms.password_weak"),
    t("forms.password_fair"),
    t("forms.password_good"),
    t("forms.password_strong"),
  ];

  // Show/hide password toggles
  const [showPassword, setShowPassword] = useState(false);
  const [showRegistrationSuccess, setShowRegistrationSuccess] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);

  // Watch password field for strength meter
  const password = watch("password", "");

  const onSubmit = async (data) => {
    setLoading(true);
    setErrorMessage("");
    try {
      const user = await registerUser(
        "private",
        data.first_name,
        data.last_name,
        data.phone,
        data.email,
        data.password,
        data.receive_notifications
      );
      if (user) {
        // TODO replace with propper alert
        setShowRegistrationSuccess(true);
      }
    } catch (error) {
      setErrorMessage(error.message || t("errors.err"));
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Helmet>
        <title>{t("seo.title.register")}</title>
        <meta name="description" content={t("seo.description.register")} />
        <meta property="og:title" content={t("seo.title.register")} />
        <meta
          property="og:description"
          content={t("seo.description.register")}
        />
      </Helmet>
      <Container
        maxWidth="lg"
        sx={{
          display: "flex",
          justifyContent: "center",
          flexDirection: "column",
          alignItems: "center",
          minHeight: "80vh",
          py: {
            xs: 0,
            sm: 8,
            md: 8,
          },
          px: {
            xs: 0,
            sm: 8,
            md: 8,
          },
          // bgcolor: 'primary.superlight',
          my: {
            xs: 0,
            sm: 2,
            md: 4,
          },
        }}
      >
        {!showRegistrationSuccess && (
          <Paper
            sx={{
              width: "100%",
              maxWidth: 600,
              border: { xs: "none", sm: "1px solid rgb(226, 234, 243)" },
              borderRadius: {
                xs: 0,
                sm: 5,
              },
              bgcolor: "#fcfcfc",
              bgcolor: "background.paper",
            }}
          >
            <Box sx={{ p: 3, pb: 0 }}>
              <Typography
                color="primary"
                variant="h2"
                sx={{ fontWeight: "bold", mb: 2, textAlign: "left" }}
              >
                {t("register_page.title")}
              </Typography>

              {errorMessage && <FormError errorMessage={errorMessage} />}
            </Box>
            <Box sx={{ p: 3, pt: 1 }}>
              <form onSubmit={handleSubmit(onSubmit)} noValidate>
                {/* First Name and Last Name side-by-side on md+ */}
                <Grid container spacing={2}>
                  <Grid item xs={12} md={6}>
                    <FormInput
                      label={t("forms.first_name")}
                      name="first_name"
                      size="normal"
                      register={register}
                      error={errors.first_name}
                      autoFocus
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <FormInput
                      label={t("forms.last_name")}
                      name="last_name"
                      size="normal"
                      register={register}
                      error={errors.last_name}
                    />
                  </Grid>
                </Grid>

                {/* Phone */}
                <FormInput
                  label={t("forms.phone")}
                  name="phone"
                  size="normal"
                  register={register}
                  error={errors.phone}
                  type="phone"
                />

                {/* Email */}
                <FormInput
                  label={t("forms.email")}
                  name="email"
                  size="normal"
                  register={register}
                  error={errors.email}
                  type="email"
                />

                {/* Password with toggle */}
                <TextField
                  label={t("forms.password")}
                  size="normal"
                  type={showPassword ? "text" : "password"}
                  variant="outlined"
                  fullWidth
                  margin="normal"
                  {...register("password")}
                  error={!!errors.password}
                  helperText={errors.password ? errors.password.message : ""}
                  inputProps={{
                    "aria-describedby": errors.password
                      ? "password-error-text"
                      : undefined,
                  }}
                  FormHelperTextProps={{ id: "password-error-text" }}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          aria-label={
                            showPassword ? "Hide password" : "Show password"
                          }
                          onClick={() => setShowPassword(!showPassword)}
                          edge="end"
                          size="small"
                          sx={{ borderRadius: "8px" }}
                        >
                          {showPassword ? <VisibilityOff /> : <Visibility />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />

                {/* Password Strength Meter */}
                {password && (
                  <Box sx={{ mb: 1 }}>
                    <Typography
                      variant="caption"
                      color={
                        passwordStrength(password) < 2
                          ? "error"
                          : passwordStrength(password) < 4
                          ? "warning.main"
                          : "success.main"
                      }
                    >
                      {t("forms.password_strength")}:{" "}
                      {strengthLabel[passwordStrength(password)]}
                    </Typography>
                    <Box
                      sx={{
                        height: 6,
                        borderRadius: 1,
                        backgroundColor: "#eee",
                        overflow: "hidden",
                        mt: 0.5,
                      }}
                    >
                      <Box
                        sx={{
                          width: `${(passwordStrength(password) / 4) * 100}%`,
                          height: "100%",
                          bgcolor:
                            passwordStrength(password) < 2
                              ? "error.main"
                              : passwordStrength(password) < 4
                              ? "warning.main"
                              : "success.main",
                          transition: "width 0.3s ease",
                        }}
                      />
                    </Box>
                  </Box>
                )}

                {/* Agree to terms */}
                <FormControl
                  error={!!errors.agree_to_terms}
                  component="fieldset"
                  variant="standard"
                  sx={{ mt: 1 }}
                  aria-describedby={
                    errors.agree_to_terms
                      ? "agree_to_terms-error-text"
                      : undefined
                  }
                >
                  <FormControlLabel
                    control={
                      <Checkbox
                        {...register("agree_to_terms")}
                        color="primary"
                      />
                    }
                    label={
                      <Typography color="primary">
                        {t("forms.agree_to_terms")}{" "}
                        <a href="noteikumi" target="_blank">
                          {t("forms.agree_to_terms_link")}
                        </a>
                      </Typography>
                    }
                  />
                  <FormHelperText id="agree_to_terms-error-text">
                    {errors.agree_to_terms ? errors.agree_to_terms.message : ""}
                  </FormHelperText>
                </FormControl>

                {/* Receive notifications
                        <FormControlLabel
                            control={<Checkbox {...register("receive_notifications")} color="primary" />}
                            label={t("register_page.receive_notifications")}
                            sx={{ mt: 1 }}
                        /> */}

                <Button
                  color="secondary"
                  type="submit"
                  disableElevation
                  fullWidth
                  variant="contained"
                  size="large"
                  sx={{
                    mt: 2,
                    mb: 1,
                    // STRIPE SHADOW
                    // boxShadow: '0px 0px 0px rgba(0,0,0,0.0)',
                    "&:hover": {
                      backgroundColor: "secondary.light",
                      // boxShadow: '0px 2px 10px rgba(0, 0, 0, 0.08), 0px 1px 3px rgba(0, 0, 0, 0.05)',
                    },
                  }}
                  disabled={loading}
                >
                  {loading ? (
                    <CircularProgress size={24} />
                  ) : (
                    t("register_page.submit")
                  )}
                </Button>

                {/* Trusted users section */}
                {/* <Box sx={{ textAlign: "center", fontSize: "0.85rem", color: "text.secondary" }}>
                            <Tooltip title={t("register_page.trusted_users_info")} arrow>
                                <Typography component="span" sx={{ cursor: "help" }}>
                                    <InfoOutlined fontSize="small" sx={{ verticalAlign: "middle", mr: 0.5 }} />
                                    {t("register_page.trusted_users")}
                                </Typography>
                            </Tooltip>
                        </Box> */}
              </form>
            </Box>
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                py: 2,
                backgroundColor: theme.palette.primary.superlight,
                borderRadius: "0 0 20px 20px",
              }}
            >
              <Link
                to="/ienakt"
                style={{
                  color: theme.palette.primary.main,
                  textDecoration: "none",
                  fontWeight: "normal",
                }}
              >
                {t("register_page.have_account")}
                <span
                  style={{ textDecoration: "underline", marginLeft: "5px" }}
                >
                  {t("register_page.sign_in")}
                </span>
              </Link>
            </Box>
          </Paper>
        )}

        {showRegistrationSuccess && (
          <Paper
            sx={{
              width: "100%",
              maxWidth: 600,
              border: { xs: "none", sm: "1px solid rgb(226, 234, 243)" },
              borderRadius: {
                xs: 0,
                sm: 5,
              },
              bgcolor: "#fcfcfc",
              bgcolor: "background.paper",
            }}
          >
            <Box sx={{ p: 3, pb: 0 }}>
              <Typography
                color="primary"
                variant="h2"
                sx={{ fontWeight: "bold", textAlign: "left" }}
              >
                {t("post_register_page.title")}
              </Typography>
            </Box>
            <Box sx={{ p: 3, pt: 1 }}>
              <Typography color="primary" variant="body">
                {t("post_register_page.subtitle")}
              </Typography>
              <br />
              <Button
                color="secondary"
                component={Link}
                to="/ienakt"
                disableElevation
                variant="contained"
                size="large"
                sx={{
                  mt: 3,
                  mb: 1,
                  "&:hover": {
                    backgroundColor: "secondary.light",
                  },
                }}
              >
                {t("post_register_page.cta")}
              </Button>
            </Box>
          </Paper>
        )}
      </Container>
    </>
  );
};

export default Register;
